import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Box, Typography,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Alert, Chip
} from '@mui/material';
import { Payments, CloudUpload } from '@mui/icons-material';
import { createUpahTukang } from '../API/upahTukang';
import { getBantuan } from '../API/bantuan';

const TAHAP_LABEL = { tahap_1: 'Tahap I (30-50%)', tahap_2: 'Tahap II (100%)' };

const ModalAddUpahTukang = ({ open, onClose, onSuccess }) => {
  const [form, setForm] = useState({ bantuanId: '', tahap: '', nominal: '', keterangan: '' });
  const [fotoFile, setFotoFile] = useState(null);
  const [bantuanOptions, setBantuanOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [progressInfo, setProgressInfo] = useState(null);

  useEffect(() => {
    if (open) { fetchBantuanOptions(); resetForm(); }
  }, [open]);

  const resetForm = () => {
    setForm({ bantuanId: '', tahap: '', nominal: '', keterangan: '' });
    setFotoFile(null);
    setError(null);
    setProgressInfo(null);
  };

  const fetchBantuanOptions = async () => {
    setLoading(true);
    try {
      const res = await getBantuan({ page: 1, limit: 200 });
      const options = (res.data?.data || []).map((b) => ({
        id: b.id,
        label: `#${b.id} - ${b.pemohon?.nama || b.namaLengkap || 'Tanpa Nama'} (${b.JenisBantuan || '-'})`,
        JenisBantuan: b.JenisBantuan
      }));
      setBantuanOptions(options);
    } catch (err) {
      console.error('Error fetching bantuan:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBantuanChange = async (bantuanId) => {
    setForm(prev => ({ ...prev, bantuanId, tahap: '' }));
    setProgressInfo(null);
    if (!bantuanId) return;

    try {
      const { getTotalUpahByBantuan } = await import('../API/upahTukang');
      const res = await getTotalUpahByBantuan(bantuanId);
      const progress = res.progressFisik ?? 0;
      const existing = res.data || [];
      const hasTahap1 = existing.some(u => u.tahap === 'tahap_1');
      const hasTahap2 = existing.some(u => u.tahap === 'tahap_2');

      setProgressInfo({ progress, hasTahap1, hasTahap2 });
    } catch (err) {
      console.error('Error fetching progress:', err);
    }
  };

  const getAvailableTahap = () => {
    if (!progressInfo) return [];
    const { progress, hasTahap1, hasTahap2 } = progressInfo;
    const available = [];
    if (progress >= 30 && progress <= 50 && !hasTahap1) available.push('tahap_1');
    if (progress >= 100 && !hasTahap2) available.push('tahap_2');
    return available;
  };

  // Pesan peringatan bila tahap belum tersedia karena progress fisik belum sesuai
  const getTahapWarning = () => {
    if (!form.bantuanId || !progressInfo) return null;
    const { progress, hasTahap1, hasTahap2 } = progressInfo;
    if (progress < 30) {
      return `Progress fisik baru ${progress}%. Silakan isi/lengkapi Progress Fisik hingga minimal 30% agar Tahap I (Upah Tukang) dapat diinput.`;
    }
    if (progress > 50 && progress < 100) {
      return `Progress fisik ${progress}% (sudah melewati 50%). Tahap I sudah terlewat. Lengkapi Progress Fisik hingga 100% agar Tahap II dapat diinput.`;
    }
    if (progress >= 30 && progress <= 50 && hasTahap1) {
      return 'Tahap I sudah diinput. Lanjutkan pengerjaan hingga Progress Fisik mencapai 100% untuk Tahap II.';
    }
    if (progress >= 100 && hasTahap2 && hasTahap1) {
      return 'Semua tahap upah tukang sudah diinput.';
    }
    return null;
  };

  const handleSubmit = async () => {
    if (!form.bantuanId) { setError('Pilih penerima bantuan'); return; }
    if (availableTahap.length === 0) {
      setError(tahapWarning || 'Tahap belum tersedia. Lengkapi Progress Fisik terlebih dahulu.');
      return;
    }
    if (!form.tahap) { setError('Pilih tahap'); return; }
    if (!form.nominal || Number(form.nominal) <= 0) { setError('Nominal harus diisi'); return; }

    setError(null);
    setSaving(true);
    try {
      await createUpahTukang({
        bantuanId: Number(form.bantuanId),
        tahap: form.tahap,
        nominal: Number(form.nominal),
        keterangan: form.keterangan,
        foto_kwitansi: fotoFile
      });
      resetForm();
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan data');
    } finally {
      setSaving(false);
    }
  };

  const availableTahap = getAvailableTahap();
  const tahapWarning = getTahapWarning();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Payments />
          <Typography variant="h6">Tambah Upah Tukang</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Pilih Penerima Bantuan *</InputLabel>
            <Select
              value={form.bantuanId}
              onChange={(e) => handleBantuanChange(e.target.value)}
              label="Pilih Penerima Bantuan *"
              disabled={loading}
            >
              <MenuItem value="">-- Pilih Bantuan --</MenuItem>
              {bantuanOptions.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {progressInfo && (
            <Chip
              label={`Progress Fisik: ${progressInfo.progress}%`}
              color={progressInfo.progress >= 100 ? 'success' : progressInfo.progress >= 30 ? 'warning' : 'default'}
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            />
          )}

          <FormControl fullWidth size="small">
            <InputLabel>Pilih Tahap *</InputLabel>
            <Select
              value={form.tahap}
              onChange={(e) => setForm(prev => ({ ...prev, tahap: e.target.value }))}
              label="Pilih Tahap *"
              disabled={!form.bantuanId}
            >
              <MenuItem value="">-- Pilih Tahap --</MenuItem>
              {availableTahap.map(t => (
                <MenuItem key={t} value={t}>{TAHAP_LABEL[t]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {form.bantuanId && availableTahap.length === 0 && progressInfo && tahapWarning && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              <Typography variant="caption">{tahapWarning}</Typography>
            </Alert>
          )}

          <TextField
            size="small"
            label="Nominal (Rp) *"
            type="number"
            value={form.nominal}
            onChange={(e) => setForm(prev => ({ ...prev, nominal: e.target.value }))}
            inputProps={{ min: 1 }}
            InputProps={{ startAdornment: <Payments sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />

          <TextField
            size="small"
            label="Keterangan (opsional)"
            multiline
            rows={2}
            value={form.keterangan}
            onChange={(e) => setForm(prev => ({ ...prev, keterangan: e.target.value }))}
          />

          <Button variant="outlined" component="label" startIcon={<CloudUpload />} size="small">
            {fotoFile ? fotoFile.name : 'Upload Foto Kwitansi (opsional)'}
            <input type="file" hidden accept="image/*" onChange={(e) => setFotoFile(e.target.files[0])} />
          </Button>
          {fotoFile && (
            <Typography variant="caption" color="text.secondary">{fotoFile.name}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>Batal</Button>
        <Button variant="contained" color="success" onClick={handleSubmit}
          disabled={saving || (!!form.bantuanId && availableTahap.length === 0)}
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}>
          {saving ? 'Menyimpan...' : 'Simpan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalAddUpahTukang;